<script lang="ts">
  /**
   * Tinder-mode review deck for stroke suspects (owner UAT 2026-07-05).
   * Right = accept suggestion · Left = the watch was right · Tap = full picker.
   * Fast-lane UI ONLY: every action goes through commitLabelChange + the
   * provenance-stamping builders — no new write path. Gesture per D6: axis
   * lock BEFORE pointer capture; buttons mirror every swipe (never swipe-only).
   */
  import { get } from "svelte/store";
  import { currentDerivedWorkout, currentLabels, saveStatus } from "./state";
  import { openOverlay, closeOverlay, activeOverlay } from "./overlay";
  import { commitLabelChange } from "./liveCorrection";
  import {
    buildStrokeCorrection, buildWatchConfirmation,
    priorLabelFor, restorePriorLabel, upsertLabel,
    resolveSwipe, lockAxis, SWIPE_THRESHOLD, TAP_SLOP, flyOffDelayMs,
    type GestureAxis,
  } from "./swipeDeckLogic";
  import {
    deckQueue, progressLabel, shortStroke, ordinal, fmtCardSeconds,
    buildMemoryStrip, type DeckItem,
  } from "./suspectDeckLogic";
  import { haptic } from "./haptics";
  import { lockBodyScroll, unlockBodyScroll } from "./native/bodyScrollLock";
  import { strokeColor } from "./designTokens";
  import { storyFor } from "./setStory";
  import { fmtTimeOfDay } from "./format";
  import type { Lap } from "./laps";
  import type { LapLabel } from "@contract/types";

  let open = $derived($activeOverlay?.kind === "suspect-deck");
  let dw = $derived($currentDerivedWorkout);

  let skipped = $state<Set<number>>(new Set());
  let resolved = $state(0);
  let undoState = $state<{ lapIndex: number; prev: LapLabel | null; summary: string } | null>(null);

  let queue = $derived(dw ? deckQueue(dw.suspects, dw.setGroups, skipped) : []);
  let current: DeckItem | null = $derived(queue[0] ?? null);
  let lap: Lap | null = $derived(
    current && dw ? dw.laps.find((l) => l.index === current!.suspect.lapIndex) ?? null : null,
  );
  // Set Memory Strip context (wave-1 Option A): the flagged length's own set,
  // so the card can show the whole-set chip strip + name + story instead of
  // a bare set number. null when the suspect has no resolvable set (mirrors
  // current.setIndex === null elsewhere in this file) — the template falls
  // back to the plain "This swim · Length N" line in that case.
  let currentSet = $derived(
    current && current.setIndex !== null && dw ? dw.setGroups[current.setIndex] ?? null : null,
  );
  let strip = $derived(currentSet && lap ? buildMemoryStrip(currentSet.laps, lap.index) : null);
  let progress = $derived(progressLabel(resolved, queue.length, skipped.size));

  // Reset session state on every open.
  $effect(() => {
    if (!open) return;
    skipped = new Set();
    resolved = 0;
    undoState = null;
    lockBodyScroll();
    return () => unlockBodyScroll();
  });

  /** Commit helper: returns true when the save was ACCEPTED (not refused). */
  function commit(next: LapLabel[]): boolean {
    commitLabelChange(next);
    if (get(saveStatus) === "failed") {
      haptic("error");
      return false;
    }
    return true;
  }

  function accept() {
    // H1 fix (wave-1 cross-vendor review): the buttons call accept()/
    // watchWasRight() DIRECTLY (no flyOff animation), while a swipe defers its
    // commit 200ms behind flyOff(). Without this guard a tap landing inside
    // that settle window commits the CURRENT card via the button, then the
    // pending timer commits whatever `current` re-derived to (the NEXT card)
    // — two commits from one gesture. flyOff() clears `settling` immediately
    // before its OWN internal call into accept()/watchWasRight(), so this
    // guard only ever blocks an EXTERNAL (button) call racing the settle window.
    if (settling) return;
    if (!current || !lap) return;
    // Capture everything undoState needs BEFORE commit(): commit() synchronously
    // re-derives currentDerivedWorkout, which re-derives `queue`/`current`/`lap`
    // (they're $derived, live getters) — reading them again after commit would
    // silently grab the NEXT queued suspect instead of the one just resolved,
    // corrupting the undo target and the toast text.
    const lapIndex = lap.index;
    const suggestedStroke = current.suspect.suggestedStroke;
    const labels = get(currentLabels);
    const prev = priorLabelFor(labels, lapIndex, lap.correctionKey);
    const label = buildStrokeCorrection(lap, suggestedStroke, prev, current.suspect);
    if (!commit(upsertLabel(labels, label))) return;
    haptic("medium");
    undoState = { lapIndex, prev, summary: `Length marked ${shortStroke(suggestedStroke)}` };
    resolved += 1;
  }

  function watchWasRight() {
    // See accept()'s comment — same H1 settling guard, same reasoning.
    if (settling) return;
    if (!current || !lap) return;
    // Same pre-commit capture as accept() — see comment there.
    const lapIndex = lap.index;
    const currentStroke = current.suspect.currentStroke;
    const labels = get(currentLabels);
    const prev = priorLabelFor(labels, lapIndex, lap.correctionKey);
    const label = buildWatchConfirmation(lap, prev, current.suspect);
    if (!commit(upsertLabel(labels, label))) return;
    haptic("medium");
    undoState = { lapIndex, prev, summary: `Kept ${shortStroke(currentStroke)}` };
    resolved += 1;
  }

  function undo() {
    // Same H1 settling guard as accept()/watchWasRight() (see accept()'s
    // comment) — a tap here can otherwise race flyOff's deferred commit and
    // land the undo against a card the settle window hasn't resolved yet.
    if (settling) return;
    if (!undoState || !dw) return;
    const target = dw.laps.find((l) => l.index === undoState!.lapIndex) ?? null;
    const next = restorePriorLabel(
      get(currentLabels), undoState.lapIndex, undoState.prev, target?.correctionKey ?? null,
    );
    if (!commit(next)) return;
    haptic("selection");
    resolved = Math.max(0, resolved - 1);
    undoState = null;
  }

  function openPicker() {
    // A suspect with no resolvable set (setIndexForLap returned null) has no
    // honest picker destination — falling back to set 0 would open the WRONG
    // set. The "Something else…" button is hidden for these; this guard is
    // defense in depth for the tap/keyboard paths. Swipes and the two big
    // buttons still resolve set-less suspects.
    if (!current || current.setIndex === null) return;
    haptic("selection");
    openOverlay({
      kind: "set-detail",
      setIndex: current.setIndex,
      fixLapIndex: current.suspect.lapIndex,
    });
  }

  function skip() {
    // Same H1 settling guard as accept()/watchWasRight() (see accept()'s
    // comment) — without it, a tap landing inside the 200ms fly-off window
    // re-derives `current` to whatever the pending commit is about to land
    // on and skips THAT card instead of the one still mid-flight.
    if (settling) return;
    if (!current) return;
    skipped = new Set([...skipped, current.suspect.lapIndex]);
  }

  function close() {
    closeOverlay("suspect-deck");
  }

  // ----- gesture: axis-lock BEFORE capture (D6 rule; ReviewCard is the anti-pattern) -----
  let dx = $state(0);
  let dragging = $state(false);
  let settling = $state(false); // true while a committed card flies off-screen
  let axis: GestureAxis = null;
  let startX = 0;
  let startY = 0;
  let vx = 0;        // px/ms, updated each pointermove
  let lastMx = 0;
  let lastT = 0;
  let dismissDy = $state(0); // downward pull distance for pull-to-dismiss
  const DISMISS_THRESHOLD = 120; // px past which release closes the deck

  // F2 fix: fly-off, springy snap-back, and the dismiss-pull spring all skip
  // their transform/transition under prefers-reduced-motion (mirrors
  // SwimHeroCard.svelte's media-query gate, done here in JS because the
  // travel distance itself — not just the easing — needs to be skipped).
  let prefersReducedMotion = $state(
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false,
  );
  $effect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => { prefersReducedMotion = mq.matches; };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  });

  function down(e: PointerEvent) {
    if (settling) return; // F1 fix: a pointerdown mid-settle must not reset dx under the flying/settling card
    dragging = true;
    axis = null;
    startX = e.clientX;
    startY = e.clientY;
    dx = 0;
    vx = 0;
    lastMx = 0;
    lastT = performance.now();
  }
  function move(e: PointerEvent) {
    if (!dragging) return;
    const mx = e.clientX - startX;
    const my = e.clientY - startY;
    if (axis === null) {
      axis = lockAxis(mx, my);
      if (axis === "horizontal") {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } else if (axis === "vertical") {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      }
    }
    if (axis === "horizontal") {
      const now = performance.now();
      const dt = now - lastT;
      if (dt > 0) vx = (mx - lastMx) / dt;
      lastMx = mx;
      lastT = now;
      dx = mx;
    } else if (axis === "vertical") {
      dismissDy = Math.max(0, my); // downward only; upward does nothing
    }
  }
  function up() {
    if (!dragging) { dx = 0; dismissDy = 0; return; }
    dragging = false;
    if (axis === "vertical") {
      const dy = dismissDy;
      dismissDy = 0;
      axis = null;
      if (dy > DISMISS_THRESHOLD) close();
      return;
    }
    const outcome = resolveSwipe(dx, vx);
    if (axis !== "horizontal" && outcome !== "tap") { dx = 0; return; }
    axis = null;
    if (outcome === "tap") { dx = 0; openPicker(); return; }
    if (outcome === "snap-back") { dx = 0; return; } // spring transition handles it
    flyOff(outcome); // keep | exclude → animate off, THEN commit
  }
  // H2 fix (wave-1 cross-vendor review): the handle for flyOff's pending
  // commit timer. Non-null for the whole window between classification (up()
  // calling flyOff) and the commit landing — cancel() reads this to decide
  // whether a commit is still in flight.
  let flyOffTimer: ReturnType<typeof setTimeout> | null = null;

  /** Fly the committed card off-screen in the swipe direction, then commit.
   *  Commit re-derives the queue (current/lap become the NEXT suspect), so it
   *  MUST run after the animation, not before. */
  function flyOff(outcome: "keep" | "exclude") {
    settling = true;
    // Reduced motion: leave dx at 0 (no visible travel) — the card simply
    // advances once the shorter delay below elapses. Same commit path either way.
    if (!prefersReducedMotion) {
      const dir = outcome === "keep" ? 1 : -1;
      dx = dir * (typeof window !== "undefined" ? window.innerWidth : 400) * 1.1;
    }
    flyOffTimer = setTimeout(() => {
      flyOffTimer = null;
      // Clear settling BEFORE calling accept()/watchWasRight() — those now
      // guard themselves on `settling` (H1), and this is the ONE legitimate
      // internal call that guard must never block. Flipping the flag first
      // is safe: JS is single-threaded, so nothing else can run between this
      // line and the call below that could race it.
      settling = false;
      if (outcome === "keep") accept();
      else watchWasRight();
      dx = 0;
    }, flyOffDelayMs(prefersReducedMotion));
  }
  /** pointercancel = the OS took the gesture back (incoming call, system
   *  overlay, gesture steal). NEVER classify it — a drag sitting past
   *  SWIPE_THRESHOLD when interrupted was not a deliberate commit. Reset only.
   *  (Mirrors SwimSwiper.svelte's dedicated cancel handler; ReviewCard's
   *  shared up-for-cancel handler is the anti-pattern.)
   *
   *  H2 fix: a classification already committed via flyOff() BEFORE this
   *  cancel can fire — up() classifies synchronously and schedules the
   *  timer; a pointercancel arriving after that (a stray/late event, or the
   *  OS revoking capture right after release) must not undo that
   *  classification. While flyOffTimer is pending, leave `settling` (and the
   *  fly-off transform in `dx`) alone — the timer is still the only thing
   *  that resolves them, so the commit lands exactly once, undisturbed. */
  function cancel() {
    dragging = false;
    axis = null;
    vx = 0;
    dismissDy = 0;
    if (flyOffTimer === null) {
      dx = 0;
      settling = false;
    }
  }

  let hintRight = $derived(dx > TAP_SLOP);
  let hintLeft = $derived(dx < -TAP_SLOP);
  let armed = $derived(Math.abs(dx) >= SWIPE_THRESHOLD);
</script>

{#if open}
  <div
    class="deck"
    role="dialog"
    aria-label="Fix strokes"
    style:transform={dismissDy ? `translateY(${dismissDy}px)` : ""}
    style:transition={dragging || prefersReducedMotion ? "none" : "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)"}
  >
    <header class="deck-head">
      <span class="deck-title">Fix strokes</span>
      {#if progress}<span class="deck-progress">{progress}</span>{/if}
      <button type="button" class="deck-close" onclick={close} aria-label="Close">✕</button>
    </header>

    {#if $saveStatus === "failed"}
      <div class="save-failed" role="alert">Couldn't save your last change. Try it again.</div>
    {/if}

    {#if current && lap}
      {@const sug = shortStroke(current.suspect.suggestedStroke)}
      {@const cur = shortStroke(current.suspect.currentStroke)}
      <div class="card-stack">
        {#if queue[1]}
          {@const nextItem = queue[1]}
          {@const nextSet = nextItem.setIndex !== null && dw ? dw.setGroups[nextItem.setIndex] ?? null : null}
          <div class="card-peek" aria-hidden="true">
            <div class="card-peek-set t-caption">
              {#if nextSet && nextItem.setIndex !== null && dw}
                {nextSet.label.toUpperCase()} · SET {nextItem.setIndex + 1} OF {dw.setGroups.length}
              {:else}
                THIS SWIM · LENGTH {nextItem.suspect.lapIndex}
              {/if}
            </div>
            <div class="card-peek-suggest">Looks more like <strong>{shortStroke(nextItem.suspect.suggestedStroke)}</strong></div>
          </div>
        {:else}
          <div class="card-peek card-peek-empty" aria-hidden="true"></div>
        {/if}
        <div
          class="card"
          class:hint-right={hintRight}
          class:hint-left={hintLeft}
          class:armed
          data-lap-index={lap.index}
          style:transform={`translateX(${dx}px) rotate(${dx * 0.05}deg)`}
          style:transition={dragging || prefersReducedMotion ? "none" : settling ? "transform 0.2s ease-in" : "transform 0.34s cubic-bezier(0.22, 1, 0.36, 1)"}
          onpointerdown={down}
          onpointermove={move}
          onpointerup={up}
          onpointercancel={cancel}
          role="button"
          tabindex="0"
          onkeydown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPicker(); } }}
          aria-label={`Length ${lap.index}, watch said ${current.suspect.currentStroke}, looks more like ${current.suspect.suggestedStroke}. Swipe right to accept, left to keep the watch's call, tap for more options.`}
        >
          {#if currentSet && strip && dw}
            {@const story = storyFor(currentSet, dw.setGroups, currentSet.aiStory)}
            <div class="card-identity" data-lap-index={lap.index}>
              <span class="card-identity-name">{currentSet.label}</span>
              <span class="card-identity-pos t-caption">SET {current.setIndex! + 1} OF {dw.setGroups.length}</span>
            </div>
            {#if story}
              <div class="card-story">{story}</div>
            {/if}
            <!-- Pure context, never a tap target — D6: a horizontal drag starting
                 on a chip must bubble straight to the card's pointer handlers, so
                 the swipe still classifies. pointer-events:none + aria-hidden keep
                 this out of the gesture surface and the accessibility tree; the
                 card's own aria-label already carries the full context. -->
            <div class="strip-block" aria-hidden="true">
              <div class="strip-caption t-caption">THIS SET, LENGTH BY LENGTH</div>
              <div class="strip-chips">
                {#if strip.overflowBefore > 0}
                  <span class="strip-chip strip-chip-more">+{strip.overflowBefore}</span>
                {/if}
                {#each strip.chips as chip (chip.lapIndex)}
                  <span
                    class="strip-chip"
                    class:strip-chip-flagged={chip.flagged}
                    style:background={strokeColor(chip.stroke)}
                  ></span>
                {/each}
                {#if strip.overflowAfter > 0}
                  <span class="strip-chip strip-chip-more">+{strip.overflowAfter}</span>
                {/if}
              </div>
              <div class="strip-marker">
                <span class="strip-marker-text">
                  &#9650; Length {lap.index}{#if current.pos}, the {ordinal(current.pos.position)} in this set{/if}
                </span>
                <span class="strip-marker-time">{fmtTimeOfDay(lap.startMs)}</span>
              </div>
            </div>
            <div class="card-divider"></div>
          {:else}
            <div class="card-set t-caption" data-lap-index={lap.index}>
              This swim · Length {lap.index}
            </div>
          {/if}
          <div class="card-watch">Watch said <strong>{cur}</strong></div>
          <div class="card-suggest">Looks more like <strong>{sug}</strong></div>
          {#if current.suspect.reasons[0]}
            <div class="card-reason">{current.suspect.reasons[0][0].toUpperCase() + current.suspect.reasons[0].slice(1)}.</div>
          {/if}
          <div class="card-meta">{lap.strokeCount !== null ? `${lap.strokeCount} strokes · ` : ""}{fmtCardSeconds(lap.durationSeconds)}s</div>
          <div class="drag-cue drag-cue-right" class:show={hintRight} aria-hidden="true">It was {sug}</div>
          <div class="drag-cue drag-cue-left" class:show={hintLeft} aria-hidden="true">Keep {cur}</div>
        </div>
      </div>

      <div class="deck-actions">
        <button type="button" class="act act-left" disabled={settling} onclick={watchWasRight}>Keep {cur}</button>
        <button type="button" class="act act-right" disabled={settling} onclick={accept}>It was {sug}</button>
      </div>
      <div class="deck-subactions">
        {#if current.setIndex !== null}
          <button type="button" class="sub" onclick={openPicker}>Something else…</button>
        {/if}
        <button type="button" class="sub" disabled={settling} onclick={skip}>Skip</button>
      </div>
    {:else}
      <div class="all-clear">
        <div class="all-clear-big">{skipped.size > 0 ? "Done for now" : "All clear"}</div>
        <div class="all-clear-sub">
          {#if skipped.size > 0}
            {resolved} sorted · {skipped.size} skipped
          {:else}
            {resolved > 0 ? `${resolved} sorted.` : "Nothing needs a look."}
          {/if}
        </div>
        {#if skipped.size > 0}
          <div class="all-clear-note">Skipped lengths stay flagged on the Swim tab.</div>
        {/if}
        <button type="button" class="all-clear-done" onclick={close}>Done</button>
      </div>
    {/if}

    {#if undoState}
      <div class="undo-bar">
        <span class="undo-text">{undoState.summary}</span>
        <button type="button" class="undo-btn" disabled={settling} onclick={undo}>Undo</button>
      </div>
    {/if}
  </div>
{/if}

<style>
  .deck {
    position: fixed; inset: 0; z-index: 61;
    display: flex; flex-direction: column; gap: 0.75rem;
    background: var(--bg-canvas); border-radius: 0;
    padding: calc(env(safe-area-inset-top) + 0.75rem) 1rem calc(1rem + env(safe-area-inset-bottom));
  }
  .deck-head { display: flex; align-items: center; gap: 0.75rem; }
  .deck-title { font-size: 1.15rem; font-weight: 800; flex: 1; }
  .deck-progress { color: var(--text-muted); font-weight: 700; }
  .deck-close {
    min-width: 44px; min-height: 44px; border: 0; border-radius: 10px;
    background: var(--bg-raised); color: var(--text-primary); font-size: 1rem;
    cursor: pointer; -webkit-tap-highlight-color: transparent;
  }
  .save-failed {
    padding: 0.6rem 0.75rem; background: var(--accent-fire); color: var(--bg-canvas);
    border-radius: 8px; font-size: 0.85rem; font-weight: 700;
  }
  /* B4's reveal-zone: padding-bottom reserves room below the current card
     for the peek strip (see .card-peek) to show through, regardless of
     whether queue[1] exists — so toggling the peek never changes deck
     height (both the populated and empty-slot peek sit in this same
     reserved space). */
  .card-stack { position: relative; padding-bottom: 3.5rem; }
  .card {
    position: relative; touch-action: none; overscroll-behavior: contain;
    /* The app-wide "Dawn Glass" look (`#app-root .card` in app.css) makes
       every `.card` 42%-opaque + blurred so the sunrise photo glows through
       behind floating content cards elsewhere in the app. This deck is a
       full opaque takeover (see .deck's solid --bg-canvas above) with no
       photo behind it — glass here only risks the B4 peek strip's text
       ghosting through. Force solid (the original, pre-Dawn-Glass --bg-card
       token, #1B1B1D); !important beats app.css's ID-scoped `#app-root
       .card` rule from this component's plain class selector. */
    background-color: #1B1B1D !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
    border: 1px solid var(--divider);
    border-radius: 16px; padding: 1.25rem;
    display: flex; flex-direction: column; gap: 0.4rem;
    user-select: none; -webkit-tap-highlight-color: transparent;
    cursor: grab; will-change: transform; min-height: 11rem;
  }
  /* B4: a faint peek of the NEXT card, anchored to the bottom of the
     reveal-zone (.card-stack's padding-bottom) so it's reliably visible
     below the current card regardless of the current card's own height (a
     reason line can make it tall enough to swallow a peek sized purely by
     scale/offset of ITS OWN box — anchoring by fixed height + bottom:0
     guarantees exposure instead). Turns the flat dead space there into a
     "there's another card" depth cue and swipe affordance. Scaled down +
     dimmed + non-interactive so it never competes with the current card. */
  .card-peek {
    position: absolute; left: 5%; right: 5%; bottom: 0; height: 4.5rem;
    padding: 0.75rem 1rem; border-radius: 14px;
    display: flex; flex-direction: column; justify-content: center; gap: 0.2rem;
    background: var(--bg-card); border: 1px solid var(--divider);
    opacity: 0.4; pointer-events: none; transform: scale(0.96);
  }
  .card-peek-set { font-size: 0.7rem; }
  .card-peek-suggest { font-size: 0.9rem; font-weight: 700; color: var(--text-primary); }
  .card-peek-empty { background: transparent; border: 1px dashed var(--divider); }
  .card.armed { border-color: var(--text-primary); }
  .card.hint-right { box-shadow: inset 6px 0 0 var(--accent-primary); }
  .card.hint-left { box-shadow: inset -6px 0 0 var(--text-muted); }
  .card-set { color: var(--text-muted); }
  /* Set Memory Strip (wave-1 Option A) — identity row, story, chip strip,
     marker line. Panel must-fix #1: every caption here is >=12px via the
     shared .t-caption token (12px/text-muted), never the smaller/dimmer
     ad-hoc sizes used elsewhere on this card (e.g. .card-peek-set). */
  .card-identity { display: flex; align-items: baseline; gap: 0.5rem; }
  .card-identity-name {
    flex: 1; min-width: 0; font-size: 1.25rem; font-weight: 700;
    color: var(--accent-primary);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .card-identity-pos { flex-shrink: 0; white-space: nowrap; }
  .card-story { font-size: 0.875rem; color: var(--text-muted); }
  /* D6: pure context, no tap target. pointer-events:none is inherited by
     every descendant chip, so a drag starting on a chip bubbles straight to
     the card's own pointer handlers instead of being swallowed here. */
  .strip-block { display: flex; flex-direction: column; gap: 0.35rem; margin-top: 0.15rem; pointer-events: none; }
  .strip-chips { display: flex; flex-wrap: wrap; align-items: center; gap: 3px; }
  .strip-chip {
    box-sizing: border-box; width: 26px; height: 26px; border-radius: 6px;
    flex-shrink: 0; border: 1px solid rgba(255, 255, 255, 0.28);
  }
  /* Panel must-fix #2: the flagged chip is carried by ring + halo + size, NOT
     hue — its fill is still the length's real stroke color (strokeColor()),
     so this survives a neighbor sharing a near hue or a colorblind/low-vision
     read. No transition/animation (deck-wide reduced-motion rule + "keep the
     strip static" instruction) — the size/ring difference is enough. */
  .strip-chip-flagged {
    width: 32px; height: 38px; border-radius: 8px;
    border: 2px solid var(--text-primary);
    box-shadow: 0 0 0 4px rgba(228, 226, 228, 0.18);
  }
  .strip-chip-more {
    width: auto; min-width: 30px; padding: 0 6px;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--bg-raised); color: var(--text-muted);
    font-size: 0.75rem; font-weight: 700; font-variant-numeric: tabular-nums;
  }
  .strip-marker { display: flex; justify-content: space-between; align-items: baseline; gap: 0.5rem; }
  .strip-marker-text { font-size: 0.8125rem; font-weight: 600; color: var(--text-primary); }
  .strip-marker-time { font-size: 0.8125rem; color: var(--text-muted); font-variant-numeric: tabular-nums; white-space: nowrap; }
  .card-divider { height: 1px; background: var(--divider); margin: 0.15rem 0; }
  .card-watch { font-size: 1rem; color: var(--text-muted); }
  .card-suggest { font-size: 1.5rem; font-weight: 800; }
  .card-reason { font-size: 0.95rem; color: var(--text-primary); }
  .card-meta { font-size: 0.8rem; color: var(--text-muted); font-variant-numeric: tabular-nums; }
  .drag-cue {
    position: absolute; top: 0.9rem; font-size: 1rem; font-weight: 800;
    padding: 0.25rem 0.6rem; border-radius: 8px; opacity: 0; transition: opacity 0.1s;
  }
  .drag-cue.show { opacity: 1; }
  .drag-cue-right { right: 0.9rem; background: var(--accent-primary); color: var(--bg-canvas); }
  .drag-cue-left { left: 0.9rem; background: var(--bg-raised); color: var(--text-primary); }
  .deck-actions { display: flex; gap: 0.6rem; }
  .act {
    flex: 1; min-height: 56px; border-radius: 12px; font-size: 1rem; font-weight: 800;
    cursor: pointer; -webkit-tap-highlight-color: transparent;
  }
  /* Identical weight for both answers — direction is communicated by position,
     labels, and the drag cues, not by which button looks more "primary". A
     filled-accent right button nudges rushed taps toward accepting the
     suggestion, biasing the acceptance data future models train on
     (steering-panel fix, 2026-07-05). */
  .act-left, .act-right { background: var(--bg-raised); color: var(--text-primary); border: 2px solid var(--text-primary); }
  /* H1 fix: disabled for the settle window between a swipe's classification
     and its deferred commit landing — a tap here would otherwise race the
     pending commit (see flyOff/cancel comments in the script block). */
  .act:disabled { opacity: 0.5; cursor: default; }
  .deck-subactions { display: flex; justify-content: space-between; }
  .sub {
    min-height: 44px; padding: 0 0.75rem; border: 0; background: none;
    color: var(--text-muted); font-size: 0.9rem; font-weight: 700; cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  /* Settle-guard disabled state (final review, wave-1) — same treatment as
     .act:disabled above, extended to Skip and Undo. */
  .sub:disabled { opacity: 0.5; cursor: default; }
  .all-clear { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; padding: 1.5rem 0; }
  .all-clear-big { font-size: 1.6rem; font-weight: 800; }
  .all-clear-sub { color: var(--text-muted); }
  .all-clear-note { color: var(--text-muted); font-size: 0.85rem; }
  .all-clear-done {
    margin-top: 0.75rem; min-height: 52px; padding: 0 2.5rem;
    background: var(--accent-primary); color: var(--bg-canvas);
    border: 0; border-radius: 12px; font-size: 1rem; font-weight: 800; cursor: pointer;
  }
  .undo-bar {
    display: flex; align-items: center; gap: 0.75rem;
    background: var(--bg-card); border: 1px solid var(--divider);
    border-radius: 10px; padding: 0.5rem 0.75rem;
  }
  .undo-text { flex: 1; font-size: 0.9rem; }
  .undo-btn {
    min-height: 44px; padding: 0 1rem; border: 2px solid var(--accent-primary);
    background: none; color: var(--accent-primary); border-radius: 10px;
    font-weight: 800; cursor: pointer; -webkit-tap-highlight-color: transparent;
  }
  /* Settle-guard disabled state (final review, wave-1) — same treatment as
     .act:disabled above, extended to Skip and Undo. */
  .undo-btn:disabled { opacity: 0.5; cursor: default; }
</style>
